import { useMemo, useState, useRef, useEffect } from "react";
import { useAppStore } from "@/stores/appStore";
import { useFieldConfig } from "@/hooks/useFieldConfig";
import { useAuth } from "@/contexts/AuthContext";
import { KanbanCard } from "./KanbanCard";
import { KANBAN_UNMAPPED_ID, type KanbanColumn } from "./columns";
import { CheckIcon, XMarkIcon, EyeSlashIcon, PlusIcon } from "@heroicons/react/24/outline";
import { ClassificationFooter } from "@/components/ui/ClassificationFooter";
import type { Initiative, SubProduct } from "@/types";

const LABEL_WIDTH = 240; // px — sticky left group-label column
const COL_WIDTH = 288; // px — each status column

interface KanbanViewProps {
  initiatives?: Initiative[];
  columns: KanbanColumn[];
  canEditColumns: boolean;
  onColumnsChange: (next: KanbanColumn[]) => void;
  onMoveInitiative: (initiativeId: string, newStatusId: string) => void;
}

// Inline-editable column header — colored top bar, click-to-rename label, hide control,
// plus a muted subline showing the mapped Status (persists even when the header is renamed).
function ColumnHeader({
  col,
  statusName,
  canEdit,
  onChange,
  onHide,
}: {
  col: KanbanColumn;
  statusName: string | null;
  canEdit: boolean;
  onChange: (patch: Partial<KanbanColumn>) => void;
  onHide: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(col.label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== col.label) onChange({ label: next });
    setEditing(false);
  };
  const cancel = () => {
    setDraft(col.label);
    setEditing(false);
  };

  return (
    <div className="flex-shrink-0 border-l border-gray-200" style={{ width: COL_WIDTH }}>
      {/* Colored top bar */}
      <div className="h-1" style={{ backgroundColor: col.color }} />
      <div className="px-3 pt-2 flex items-center gap-2 min-w-0">
        {canEdit && (
          <label className="relative flex-shrink-0" title="Column color">
            <span
              className="block w-3 h-3 rounded-full border border-gray-300 cursor-pointer"
              style={{ backgroundColor: col.color }}
            />
            <input
              type="color"
              value={col.color}
              onChange={(e) => onChange({ color: e.target.value })}
              className="absolute inset-0 opacity-0 cursor-pointer w-3 h-3"
            />
          </label>
        )}

        {editing ? (
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") cancel();
              }}
              className="min-w-0 flex-1 px-1.5 py-0.5 text-sm font-semibold border border-blue-400 rounded outline-none"
            />
            <button onClick={commit} className="p-0.5 text-green-600 hover:bg-green-50 rounded flex-shrink-0" title="Save">
              <CheckIcon className="w-4 h-4" />
            </button>
            <button onClick={cancel} className="p-0.5 text-gray-500 hover:bg-gray-100 rounded flex-shrink-0" title="Cancel">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <span
              className={`text-sm font-semibold text-text-primary truncate min-w-0 flex-1 ${canEdit ? "cursor-text hover:text-blue-600" : ""}`}
              onClick={() => canEdit && setEditing(true)}
              title={canEdit ? "Click to rename" : col.label}
            >
              {col.label}
            </span>
            {canEdit && (
              <button
                onClick={onHide}
                className="p-0.5 text-text-tertiary hover:text-text-primary hover:bg-gray-100 rounded flex-shrink-0"
                title="Hide column"
              >
                <EyeSlashIcon className="w-4 h-4" />
              </button>
            )}
          </>
        )}
      </div>
      {/* Mapped status subline */}
      <div className="px-3 pb-2">
        <span className="text-[11px] text-text-tertiary truncate block" title={statusName ? `Status: ${statusName}` : "Unmapped"}>
          {statusName ? `Status: ${statusName}` : "Unmapped"}
        </span>
      </div>
    </div>
  );
}

export function KanbanView({
  initiatives: filteredInitiatives,
  columns,
  canEditColumns,
  onColumnsChange,
  onMoveInitiative,
}: KanbanViewProps) {
  const { products, initiatives: allInitiatives, quarters, statuses, openViewInitiativeModal } = useAppStore();
  const fc = useFieldConfig();
  const { user } = useAuth();
  const initiatives = filteredInitiatives || allInitiatives;

  const statusName = (statusId: string | null): string | null =>
    statusId ? statuses.find((s: any) => s.id === statusId)?.name ?? null : null;

  // Can the current user drag (i.e. change status of) this initiative?
  const canEditInit = (init: Initiative): boolean => {
    if (!user) return false;
    if (user.role === "ADMIN") return true;
    if (user.role === "EDITOR") return !(init as any).userId || (init as any).userId === user.id;
    return false;
  };

  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);

  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.hidden).slice().sort((a, b) => a.order - b.order),
    [columns]
  );
  const hiddenColumns = useMemo(
    () => columns.filter((c) => c.hidden).slice().sort((a, b) => a.order - b.order),
    [columns]
  );

  // First visible column mapped to each status — where a card of that status lands
  const statusToColumnId = useMemo(() => {
    const m = new Map<string, string>();
    visibleColumns.forEach((c) => {
      if (c.statusId && !m.has(c.statusId)) m.set(c.statusId, c.id);
    });
    return m;
  }, [visibleColumns]);

  // Chronological position of a start quarter for in-cell sorting
  const startPos = (init: Initiative) => {
    const idx = quarters.findIndex((q: any) => q.key === (init as any).startQuarter);
    return idx < 0 ? Number.MAX_SAFE_INTEGER : idx;
  };

  // Group by sub-product → column id, each cell sorted by start month asc (then createdAt asc).
  // A card lands in the first visible column mapped to its status, or the "Unmapped" column.
  const cardsByCell = useMemo(() => {
    const map = new Map<string, Map<string, Initiative[]>>(); // subProductId -> columnId -> inits
    initiatives.forEach((init) => {
      const sp = (init as any).subProductId;
      const statusId = (init as any).statusId;
      const columnId = (statusId && statusToColumnId.get(statusId)) || KANBAN_UNMAPPED_ID;
      if (!map.has(sp)) map.set(sp, new Map());
      const byColumn = map.get(sp)!;
      if (!byColumn.has(columnId)) byColumn.set(columnId, []);
      byColumn.get(columnId)!.push(init);
    });
    map.forEach((byColumn) => {
      byColumn.forEach((inits) => {
        inits.sort(
          (a, b) =>
            startPos(a) - startPos(b) ||
            new Date((a as any).createdAt).getTime() - new Date((b as any).createdAt).getTime()
        );
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiatives, quarters, statusToColumnId]);

  // Any initiative whose status maps to no visible column → show a read-only "Unmapped" column
  const hasUnmapped = useMemo(
    () => initiatives.some((i) => !(i as any).statusId || !statusToColumnId.has((i as any).statusId)),
    [initiatives, statusToColumnId]
  );

  const subProductsByProduct = useMemo(() => {
    const grouped = new Map<string, SubProduct[]>();
    products.forEach((product: any) => {
      if (product.subProducts) grouped.set(product.id, product.subProducts);
    });
    return grouped;
  }, [products]);

  const subProductInitiativeCount = (subProductId: string): number => {
    const byColumn = cardsByCell.get(subProductId);
    if (!byColumn) return 0;
    let n = 0;
    byColumn.forEach((arr) => (n += arr.length));
    return n;
  };

  const patchColumn = (columnId: string, patch: Partial<KanbanColumn>) => {
    onColumnsChange(columns.map((c) => (c.id === columnId ? { ...c, ...patch } : c)));
  };
  const setHidden = (columnId: string, hidden: boolean) => patchColumn(columnId, { hidden });

  const handleDrop = (e: React.DragEvent, columnId: string, targetStatusId: string | null) => {
    e.preventDefault();
    setDragOverColumnId(null);
    if (!targetStatusId || columnId === KANBAN_UNMAPPED_ID) return; // only mapped columns accept drops
    const id = e.dataTransfer.getData("text/plain");
    const fromStatus = e.dataTransfer.getData("application/x-from-status");
    if (id && targetStatusId !== fromStatus) {
      onMoveInitiative(id, targetStatusId);
    }
  };

  // Products (and sub-products) that actually have cards, to keep the board focused
  const productsWithCards = products.filter((p: any) =>
    (subProductsByProduct.get(p.id) || []).some((sp) => subProductInitiativeCount(sp.id) > 0)
  );

  const totalColumns = visibleColumns.length + (hasUnmapped ? 1 : 0);
  const boardWidth = LABEL_WIDTH + totalColumns * COL_WIDTH;

  const renderCell = (subProductId: string, columnId: string, targetStatusId: string | null) => {
    const droppable = !!targetStatusId && columnId !== KANBAN_UNMAPPED_ID;
    const cards = cardsByCell.get(subProductId)?.get(columnId) || [];
    const isDragOver = droppable && dragOverColumnId === columnId;
    return (
      <div
        key={columnId}
        className={`flex-shrink-0 border-l p-2 space-y-2 transition-colors ${
          isDragOver ? "border-l-blue-400 bg-blue-50/60" : "border-l-gray-100"
        }`}
        style={{ width: COL_WIDTH }}
        onDragOver={(e) => {
          if (!droppable) return;
          e.preventDefault();
          if (dragOverColumnId !== columnId) setDragOverColumnId(columnId);
        }}
        onDrop={(e) => handleDrop(e, columnId, targetStatusId)}
      >
        {cards.map((init) => {
          const canDrag = canEditInit(init);
          return (
            <KanbanCard
              key={init.id}
              initiative={init}
              draggable={canDrag}
              onClick={() => openViewInitiativeModal(init)}
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", init.id);
                e.dataTransfer.setData("application/x-from-status", (init as any).statusId || "");
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => setDragOverColumnId(null)}
            />
          );
        })}
      </div>
    );
  };

  if (productsWithCards.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center text-text-secondary text-sm">
          No initiatives match the current filters.
        </div>
        <ClassificationFooter />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Hidden-column chips — inline re-show without opening settings */}
      {canEditColumns && hiddenColumns.length > 0 && (
        <div className="px-6 py-2 flex items-center gap-2 flex-wrap border-b border-gray-100 flex-shrink-0">
          <span className="text-xs text-text-tertiary">Hidden:</span>
          {hiddenColumns.map((c) => (
            <button
              key={c.id}
              onClick={() => setHidden(c.id, false)}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-text-secondary hover:bg-gray-200"
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
              {c.label}
              <PlusIcon className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto scrollbar-thin">
        <div className="relative" style={{ minWidth: boardWidth }}>
          {/* Header row */}
          <div className="sticky top-0 z-30 flex bg-background-primary border-b border-gray-200">
            <div
              className="sticky left-0 z-40 bg-background-secondary border-r border-gray-200 flex items-end px-4 py-2"
              style={{ width: LABEL_WIDTH }}
            >
              <span className="text-sm font-semibold text-text-primary">
                {fc.label('product')} / {fc.label('subProduct')}
              </span>
            </div>
            {visibleColumns.map((col) => (
              <ColumnHeader
                key={col.id}
                col={col}
                statusName={statusName(col.statusId)}
                canEdit={canEditColumns}
                onChange={(patch) => patchColumn(col.id, patch)}
                onHide={() => setHidden(col.id, true)}
              />
            ))}
            {hasUnmapped && (
              <div className="flex-shrink-0 border-l border-gray-200" style={{ width: COL_WIDTH }}>
                <div className="h-1 bg-gray-300" />
                <div className="px-3 pt-2">
                  <span className="text-sm font-semibold text-text-tertiary">No column</span>
                </div>
                <div className="px-3 pb-2">
                  <span className="text-[11px] text-text-tertiary block">Drag a card out to assign a column</span>
                </div>
              </div>
            )}
          </div>

          {/* Product groups */}
          {productsWithCards.map((product: any) => {
            const sps = (subProductsByProduct.get(product.id) || []).filter(
              (sp) => subProductInitiativeCount(sp.id) > 0
            );
            return (
              <div key={product.id}>
                {/* Product band */}
                <div className="flex bg-blue-50/50 border-b border-gray-100">
                  <div
                    className="sticky left-0 z-10 bg-blue-50/50 border-r border-gray-200 px-4 py-2 min-w-0"
                    style={{ width: LABEL_WIDTH }}
                  >
                    <span className="text-sm font-semibold text-text-primary truncate block" title={product.name}>{product.name}</span>
                  </div>
                  <div className="flex-1" />
                </div>

                {/* Sub-product rows */}
                {sps.map((sp) => (
                  <div key={sp.id} className="flex border-b border-gray-100">
                    <div
                      className="sticky left-0 z-10 bg-background-primary border-r border-gray-200 px-3 py-2 pl-8 flex items-center min-w-0"
                      style={{ width: LABEL_WIDTH }}
                    >
                      <span className="text-sm font-medium text-text-secondary truncate" title={sp.name}>{sp.name}</span>
                    </div>
                    {visibleColumns.map((col) => renderCell(sp.id, col.id, col.statusId))}
                    {hasUnmapped && renderCell(sp.id, KANBAN_UNMAPPED_ID, null)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <ClassificationFooter />
    </div>
  );
}
